import { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  VStack,
  useColorModeValue
} from "@chakra-ui/react";
import {
  CloseIcon,
  SearchIcon
} from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import {
  FaCaretDown,
  FaCaretUp
} from "react-icons/fa";

const getDisplayValue = (item, displayField) => {
  if (!item) return "";

  if (Array.isArray(displayField)) {
    return displayField
      .map(field => item?.[field] ?? "")
      .join(" - ")
      .trim();
  }

  return item?.[displayField] ?? "";
};

const SearchSelect = ({
                        id,
                        items,
                        displayField,
                        valueField,
                        placeholder,
                        value,
                        onChange,
                        required,
                      }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filteredItems, setFilteredItems] = useState(items);
  const wrapperRef = useRef(null);
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState("");

  const bgColor = useColorModeValue("white", "navy.900");
  const inputTextColor = useColorModeValue("gray.900", "white");
  const placeholderColor = useColorModeValue("gray.300", "gray.400");
  const borderColor = useColorModeValue("gray.200", "navy.700");
  const hoverColor = useColorModeValue("gray.100", "gray.600");
  const dropdownTextColor = useColorModeValue("gray.700", "gray.200");

  useEffect(() => {
    if (isOpen) {
      setFilteredItems(items);
    }
  }, [isOpen, items]);

  useEffect(() => {
    setSelectedValue(value || "");
  }, [value]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);

    setFilteredItems(
      items.filter((item) => {
        if (Array.isArray(displayField)) {
          const concatenatedFields = displayField
            .map((field) => {
              const fieldValue = item[field];
              return fieldValue != null ? String(fieldValue) : '';
            })
            .join(' - ');

          return concatenatedFields.toLowerCase().includes(value.toLowerCase().trim());
        }

        const fieldValue = item[displayField];

        if (fieldValue != null) {
          return String(fieldValue).toLowerCase().includes(value.toLowerCase().trim());
        }

        return false;
      })
    );
  };

  const changeEvent = ({ id, value }) => ({
    type: "change",
    persist: () => {},
    target: {
      id: id,
      value: value,
    },
    currentTarget: {
      id: id,
      value: value,
    },
  })

  const handleSelect = (item) => {
    if (typeof item === "object" && item !== null && valueField in item) {
      const value = item[valueField];
      setIsOpen(false);
      setSelectedValue(value);
      onChange(changeEvent({ id, value }));
    } else {
      console.error(t("system.notifications.error"), item);
    }
  };

  const handleClearSelection = () => {
    setSearch("");
    setSelectedValue("");
    onChange(changeEvent({ id, value: "" }));
  };

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <VStack align="stretch" spacing={2} width="100%" ref={wrapperRef}>
      <InputGroup>
        <Input
          placeholder={placeholder}
          fontSize="sm"
          py="18px"
          height="32px"
          color={inputTextColor}
          bg={bgColor}
          borderColor={borderColor}
          borderRadius="8px"
          cursor="pointer"
          _placeholder={{ color: placeholderColor, fontSize: "sm" }}
          id={id}
          value={
            selectedValue
                ? getDisplayValue(
                    Array.isArray(items) ? items.find(item => String(item[valueField]) === String(selectedValue)) : null,
                    displayField
                )
                : ""
          }
          readOnly
          onClick={() => setIsOpen(true)}
          required={required}
        />
        {value && (
          <InputRightElement>
            <IconButton
              icon={<CloseIcon bow={2} h={2} />}
              size="sm"
              variant="ghost"
              onClick={handleClearSelection}
              aria-label={t("system.actions.clear")}
            />
          </InputRightElement>
        )}
        {!value && (
          <InputRightElement>
            <Box color={placeholderColor}>
              <FaCaretDown w={2} h={2} />
            </Box>
          </InputRightElement>
        )}
      </InputGroup>

      {isOpen && (
        <Box
          border="1px solid"
          borderColor={borderColor}
          borderRadius="8px"
          boxShadow="sm"
          bg={bgColor}
          px={4}
          py={2}
          width="100%"
          position="absolute"
          zIndex="10"
        >
          <Box htmlFor="search" mb={2} p={0} width="100%" color={placeholderColor}>
            <Flex justify="space-between" align="center">
              <Box fontSize="sm">{placeholder}</Box>
              <Box>
                <FaCaretUp fontSize="sm" />
              </Box>
            </Flex>
          </Box>

          <InputGroup>
            <Input
              placeholder={t("system.placeholders.search")}
              value={search || " "}
              bg={bgColor}
              onChange={handleSearchChange}
              mb={2}
              py="18px"
              borderColor={borderColor}
              fontSize="sm"
              height="28px"
              color={inputTextColor}
              _placeholder={{ color: placeholderColor, fontSize: "sm" }}
              onFocus={(e) => e.stopPropagation()}
            />
            <InputRightElement>
              <SearchIcon color={placeholderColor} />
            </InputRightElement>
          </InputGroup>

          <List maxHeight="200px" overflowY="auto" fontSize="sm" color={dropdownTextColor}>
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <ListItem
                  key={item[valueField]}
                  onClick={() => handleSelect(item)}
                  bg={bgColor}
                  padding="6px"
                  cursor="pointer"
                  _hover={{ bg: hoverColor }}
                >
                  {getDisplayValue(item, displayField)}
                </ListItem>
              ))
            ) : (
              <ListItem padding="6px" color={placeholderColor}>
                {t("system.messages.info.no-data")}
              </ListItem>
            )}
          </List>
        </Box>
      )}
    </VStack>
  );
};

export default SearchSelect;
